// Owner: shell-nav (plan §3.1, §9.2). The workspace rail: workspace switcher, "Search or ask… ⌘K",
// "✦ Ask AI ⌘J", Home, Inbox, Library, the Data section (collapsible app groups with the objects the
// member may read), Pinned (drag to reorder, right-click to unpin), Recent (server-side), and at the
// bottom Automations (builders), Settings and the account menu. Collapses to 56 px with ⌘\ and peeks
// open on hover or keyboard focus; collapsed automatically below 1200 px until the member chooses.
import { useQuery } from '@tanstack/react-query';
import { Link, useLinkProps, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  type ComponentProps,
  type ReactElement,
  type Ref,
  cloneElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { NavBadge, NavItem } from '@dataroom/core';
import { DropIndicator, useSortableList } from '@dataroom/interaction';
import {
  Avatar,
  ContextMenu,
  ICON_PATHS,
  Icon,
  type IconName,
  Menu,
  type MenuItem,
  Tooltip,
  lazyLayer,
  objectColors,
  objectIcons,
} from '@dataroom/ui';
import { viewsApi } from '@dataroom/api-client/views';
import { AiRailSlot } from '../ai/AiRailSlot';
import { AskAiButton } from '../ai/AskAiButton';
import { AccountMenu } from '../features/account/AccountMenu';
import type { NewObjectModal as NewObjectModalType } from '../features/data-model/NewObjectModal';
import { api } from '../lib/api';
import { formatShortcut, hasMessage, t } from '../lib/i18n';
import { useModuleOn } from '../lib/modules';
import { useMe, useModel, useNav, useRecent } from '../lib/queries';
import { appActiveKey, libraryPath } from './nav';
import { openCommandPalette } from './overlays';
import { pinMatches, useCurrentPin, usePins } from './pins';
import type { RailControl } from './rail-state';
import { useHasRoute } from './routes';
import { useWorkspaces } from './workspace';
import { useBrandKit } from '../features/branding/api';
import { useSearchLabel } from './search-label';
import { previewExtended, usePreviewState } from '../features/copilot/preview-store';

const { Popup: NewObjectModal, prefetch: prefetchNewObject } = lazyLayer<
  ComponentProps<typeof NewObjectModalType>
>(() => import('../features/data-model/NewObjectModal').then((m) => ({ default: m.NewObjectModal })));

const viewsClient = viewsApi(api.http);

/** An icon name of the kit, or a neutral fallback for names the kit does not know. */
export function iconOf(name: string | null | undefined, fallback: IconName = 'box'): IconName {
  if (!name) return fallback;
  if (name in objectIcons) return objectIcons[name as keyof typeof objectIcons];
  return name in ICON_PATHS ? (name as IconName) : fallback;
}

/** The rail tint of an object colour (neutral when the palette does not know it). */
export function railTint(color: string | null | undefined): string | undefined {
  const c = color ? (objectColors as Record<string, { rail?: string } | undefined>)[color] : undefined;
  return c?.rail;
}

function Badge({ badge }: { badge: NavBadge | undefined }) {
  if (!badge) return null;
  return <span className={`rail-badge ${badge.tone === 'plain' ? '' : badge.tone}`}>{badge.value}</span>;
}

/** Tooltip with the label while the rail is collapsed (the label itself is hidden then). */
function RailTip({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean;
  label: string;
  children: ReactElement<{ 'aria-label'?: string }>;
}) {
  if (!collapsed) return children;
  // Collapsed, the visible label is gone: the item keeps its name for assistive tech.
  const named = children.props['aria-label'] ? children : cloneElement(children, { 'aria-label': label });
  return (
    <Tooltip content={label} placement="right">
      {named}
    </Tooltip>
  );
}

function ItemBody({
  icon,
  tint,
  label,
  badge,
}: {
  icon: IconName;
  tint?: string | undefined;
  label: string;
  badge?: NavBadge | undefined;
}) {
  return (
    <>
      <Icon name={icon} className="rail-icon" style={tint ? { stroke: tint } : undefined} />
      <span className="rail-label">{label}</span>
      <Badge badge={badge} />
    </>
  );
}

export type AppSidebarProps = {
  rail: RailControl;
  /** The rail is shown expanded over the content while collapsed (hover or keyboard focus). */
  peek: boolean;
};

/**
 * Whether a scroll area has more content below its fold: the rail fades its last visible line out
 * instead of cutting a heading or hint in half above the fixed bottom block.
 */
function useMoreBelow(ref: { current: HTMLElement | null }): boolean {
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setMore(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    const mo = new MutationObserver(check);
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
      mo.disconnect();
    };
  }, [ref]);
  return more;
}

/** Workspace navigation rail (design/screens/AppSidebar.dc.html). */
export function AppSidebar({ rail, peek }: AppSidebarProps) {
  const searchLabel = useSearchLabel();
  useEffect(() => prefetchNewObject(), []);
  const scrollRef = useRef<HTMLDivElement>(null);
  const more = useMoreBelow(scrollRef);
  const navQuery = useNav();
  const nav = navQuery.data;
  // Objects a change shown in preview mode adds (outlined like the plan's ghost cards).
  const previewSession = usePreviewState().session;
  const previewObjects = previewSession?.objects;
  const extendedObjects = useMemo(() => previewExtended(previewSession), [previewSession]);
  // They are often at the end of the rail ("Other"): brought into view when the preview starts.
  const previewKey = [...(previewObjects ?? []), ...extendedObjects].join(',');
  useEffect(() => {
    if (!previewKey || !nav) return;
    scrollRef.current?.querySelector('[data-preview]')?.scrollIntoView?.({ block: 'center' });
  }, [previewKey, nav]);
  const model = useModel().data;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = appActiveKey(pathname);
  const hasRoute = useHasRoute();
  const [addTo, setAddTo] = useState<string | null>(null);
  const collapsed = rail.collapsed && !peek;
  const badges = nav?.badges ?? {};
  const canBuild = nav?.can.build ?? false;
  // Data quality (conflicts, duplicates, failed checks) is a work queue: builders always see it, others while it has open items.
  const canReviewData = canBuild || Boolean(badges.quality);
  const library = libraryPath(hasRoute);
  // Optional workspace modules (Settings › General › Modules): no rail item while one is off.
  const meetingsOn = useModuleOn('meetings');
  const activitiesOn = useModuleOn('activities');

  const primary: Array<{ key: string; to: string; icon: IconName; label: string; badge?: NavBadge }> = [
    { key: 'home', to: '/', icon: 'home', label: t('shell.nav.home') },
    {
      key: 'inbox',
      to: '/inbox',
      icon: 'inbox',
      label: t('shell.nav.inbox'),
      ...(badges.inbox ? { badge: badges.inbox } : {}),
    },
    ...(meetingsOn
      ? [{ key: 'meetings', to: '/meetings', icon: 'calendar' as const, label: t('shell.nav.meetings') }]
      : []),
    ...(activitiesOn
      ? [{ key: 'engagements', to: '/engagements', icon: 'pulse' as const, label: t('egx.nav.activities') }]
      : []),
    { key: 'library', to: library, icon: 'folder', label: t('shell.nav.library') },
  ];

  return (
    <nav
      className="rail"
      aria-label={t('shell.nav.workspaceNavigation')}
      data-testid="app-sidebar"
      data-collapsed={collapsed || undefined}
    >
      <div className="rail-top">
        <div className="rail-ws-row">
          <WorkspaceSwitcher collapsed={collapsed} />
          {!collapsed ? (
            <Tooltip content={t('shell.cmd.toggleRail')} shortcut="mod+\\" placement="right">
              <button
                type="button"
                className="rail-collapse"
                aria-label={rail.collapsed ? t('shell.nav.expand') : t('shell.nav.collapse')}
                aria-expanded={!rail.collapsed}
                onClick={rail.toggle}
                data-testid="rail-toggle"
              >
                <Icon name="columns" size={15} />
              </button>
            </Tooltip>
          ) : null}
        </div>
        <RailTip collapsed={collapsed} label={searchLabel}>
          <button
            type="button"
            className="rail-search"
            onClick={openCommandPalette}
            data-testid="nav-search"
            aria-label={collapsed ? searchLabel : undefined}
            aria-keyshortcuts="Meta+K Control+K"
          >
            <Icon name="search" size={15} />
            <span className="rail-label">{searchLabel}</span>
            <kbd className="rail-kbd">{formatShortcut('mod+k')}</kbd>
          </button>
        </RailTip>
        {/* AI only where it works: nothing for members while it is off, "Connect AI" for admins. */}
        {collapsed ? null : (
          // The slot of the ai-client contract: collapses for members, "Connect AI" for admins.
          <AiRailSlot connectClassName="rail-ask rail-connect">
            <AskAiButton variant="rail" className="rail-ask" />
          </AiRailSlot>
        )}
      </div>

      <div className="rail-scroll" ref={scrollRef} data-more={more || undefined}>
        <div className="rail-group">
          {primary.map((i) => (
            <RailTip key={i.key} collapsed={collapsed} label={i.label}>
              <Link
                to={i.to}
                className={`rail-item${active === i.key ? ' active' : ''}`}
                aria-current={active === i.key ? 'page' : undefined}
                data-testid={`nav-${i.key}`}
              >
                <ItemBody icon={i.icon} label={i.label} badge={i.badge} />
              </Link>
            </RailTip>
          ))}
        </div>

        {(nav?.componentApps ?? []).length ? (
          <div
            className="rail-section"
            role="group"
            aria-labelledby="rail-apps-title"
            data-testid="nav-component-apps"
          >
            <h2 className="rail-section-title" id="rail-apps-title">
              {t('shell.nav.apps')}
            </h2>
            {(nav?.componentApps ?? []).map((a) => (
              <RailTip key={a.key} collapsed={collapsed} label={a.label}>
                <Link
                  to="/apps/$key"
                  params={{ key: a.key }}
                  className={`rail-item${active === `app:${a.key}` ? ' active' : ''}`}
                  aria-current={active === `app:${a.key}` ? 'page' : undefined}
                  data-testid={`nav-app-component-${a.key}`}
                >
                  <ItemBody icon={iconOf(a.icon, 'puzzle')} label={a.label} />
                </Link>
              </RailTip>
            ))}
          </div>
        ) : null}

        {(nav?.apps ?? []).length ? (
          <div className="rail-section" role="group" aria-labelledby="rail-data-title" data-testid="nav-data">
            <h2 className="rail-section-title" id="rail-data-title">
              {t('shell.nav.data')}
            </h2>
            {(nav?.apps ?? []).map((app) => {
              const folded = rail.collapsedGroups.includes(app.key) && !collapsed;
              return (
                <div className="rail-group" key={app.key} data-testid={`nav-app-${app.key}`}>
                  {!collapsed ? (
                    <div className="rail-group-title">
                      <button
                        type="button"
                        className="rail-group-toggle"
                        aria-expanded={!folded}
                        onClick={() => rail.toggleGroup(app.key)}
                        data-testid={`nav-app-toggle-${app.key}`}
                      >
                        <Icon name="chevron-right" size={11} className="rail-chevron" />
                        <span>{app.label}</span>
                      </button>
                      {canBuild && app.key !== '_other' ? (
                        <button
                          type="button"
                          className="rail-group-add"
                          aria-label={t('shell.nav.addObject', { app: app.label })}
                          onClick={() => setAddTo(app.key)}
                          data-testid={`nav-add-object-${app.key}`}
                        >
                          <Icon name="plus" size={13} />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {folded
                    ? null
                    : app.objects.map((o) => {
                        const key = `object:${o.key}`;
                        const isActive = active === key;
                        return (
                          <RailTip key={o.key} collapsed={collapsed} label={o.label}>
                            <Link
                              to="/o/$object"
                              params={{ object: o.key }}
                              className={`rail-item${isActive ? ' active' : ''}`}
                              aria-current={isActive ? 'page' : undefined}
                              data-testid={`nav-object-${o.key}`}
                              data-preview={
                                previewObjects?.includes(o.key)
                                  ? 'new'
                                  : extendedObjects.includes(o.key)
                                    ? 'extended'
                                    : undefined
                              }
                            >
                              <ItemBody icon={iconOf(o.icon)} tint={railTint(o.color)} label={o.label} />
                            </Link>
                          </RailTip>
                        );
                      })}
                </div>
              );
            })}
          </div>
        ) : !navQuery.isPending ? null : (
          // The rail keeps its shape while `/nav` loads: placeholder rows where the objects will be.
          <div className="rail-section" aria-busy="true" data-testid="nav-data-loading">
            {!collapsed ? (
              <span className="rail-placeholder rail-placeholder-title" aria-hidden="true" />
            ) : null}
            {[72, 56, 64, 48].map((w) => (
              <span key={w} className="rail-placeholder-row" aria-hidden="true">
                <span className="rail-placeholder rail-placeholder-icon" />
                {!collapsed ? <span className="rail-placeholder" style={{ width: `${w}%` }} /> : null}
              </span>
            ))}
          </div>
        )}

        {nav && !collapsed ? <RecentSection /> : null}
      </div>

      {nav && !collapsed ? (
        <div className="rail-pinned-dock">
          <PinnedSection />
        </div>
      ) : null}

      <div className="rail-bottom">
        {canReviewData ? (
          <RailTip collapsed={collapsed} label={t('shell.nav.dataQuality')}>
            <Link
              to="/data-quality"
              className={`rail-item${active === 'data-quality' ? ' active' : ''}`}
              aria-current={active === 'data-quality' ? 'page' : undefined}
              data-testid="nav-data-quality"
            >
              <ItemBody icon="check" label={t('shell.nav.dataQuality')} badge={badges.quality} />
            </Link>
          </RailTip>
        ) : null}
        {canBuild ? (
          <RailTip collapsed={collapsed} label={t('shell.nav.components')}>
            <Link
              to="/apps"
              className={`rail-item${active === 'components' ? ' active' : ''}`}
              aria-current={active === 'components' ? 'page' : undefined}
              data-testid="nav-components"
            >
              <ItemBody icon="puzzle" label={t('shell.nav.components')} />
            </Link>
          </RailTip>
        ) : null}
        {canBuild ? (
          <RailTip collapsed={collapsed} label={t('shell.nav.automations')}>
            <Link
              to="/automations"
              className={`rail-item${active === 'automations' ? ' active' : ''}`}
              aria-current={active === 'automations' ? 'page' : undefined}
              data-testid="nav-automations"
            >
              <ItemBody icon="bolt" label={t('shell.nav.automations')} badge={badges.automations} />
            </Link>
          </RailTip>
        ) : null}
        <RailTip collapsed={collapsed} label={t('shell.nav.settings')}>
          <Link
            to="/settings"
            className={`rail-item${active === 'settings' ? ' active' : ''}`}
            aria-current={active === 'settings' ? 'page' : undefined}
            data-testid="nav-settings"
          >
            <ItemBody icon="sliders" label={t('shell.nav.settings')} />
          </Link>
        </RailTip>
        <RailAccount collapsed={collapsed} />
      </div>
      {addTo && model ? (
        <NewObjectModal
          open
          onClose={() => setAddTo(null)}
          apps={model.apps}
          initialAppId={model.apps.find((a) => a.key === addTo)?.id ?? ''}
        />
      ) : null}
    </nav>
  );
}

// -- Workspace switcher ------------------------------------------------------------------------------

function WorkspaceSwitcher({ collapsed }: { collapsed: boolean }) {
  const { me, all, switchTo, switching } = useWorkspaces();
  // The brand kit's mark (Settings › Branding); the initial on the accent without one.
  const mark = useBrandKit().data?.logos.mark?.url ?? null;
  const navigate = useNavigate();
  const nav = useNav().data;
  const canSettings = me.permissions.some(
    (p) => p === '*' || p === 'workspace.admin' || p === 'settings.read',
  );
  const list = all.length
    ? all
    : [{ id: me.workspace.id, name: me.workspace.name, roleLabel: me.member.roleLabel, current: true }];
  const items: MenuItem[] = [
    { type: 'label', label: t('shell.workspaces') },
    ...list.map((w) => ({
      label: w.name,
      description: w.roleLabel,
      // The current workspace carries the check; the others are plain items.
      ...(w.current ? { checked: true } : {}),
      disabled: switching && !w.current,
      onSelect: () => (w.current ? undefined : switchTo(w.id)),
    })),
    ...(nav?.can.build || canSettings ? [{ type: 'separator' as const }] : []),
    ...(nav?.can.build
      ? [
          {
            label: t('shell.newWorkspace'),
            icon: 'plus' as const,
            onSelect: () => void navigate({ to: '/onboarding' }),
          },
        ]
      : []),
    ...(canSettings
      ? [
          {
            label: t('shell.workspaceSettings'),
            icon: 'sliders' as const,
            onSelect: () => void navigate({ to: '/settings/general' }),
          },
        ]
      : []),
  ];
  return (
    <Menu
      label={t('shell.switchWorkspace')}
      width={256}
      items={items}
      placement="bottom-start"
      trigger={
        <button
          type="button"
          className="rail-ws"
          aria-label={t('shell.switchWorkspaceOf', { workspace: me.workspace.name })}
          data-testid="workspace-switcher"
        >
          <span className="rail-ws-logo" aria-hidden="true" data-mark={mark ? true : undefined}>
            {mark ? <img src={mark} alt="" /> : me.workspace.name.charAt(0)}
          </span>
          {collapsed ? null : (
            <>
              <span className="rail-ws-text">
                <span className="rail-ws-name">{me.workspace.name}</span>
                <span className="rail-ws-env">{environmentLabel(me.workspace.environment)}</span>
              </span>
              <Icon name="chevron-updown" size={14} className="rail-ws-chevron" />
            </>
          )}
        </button>
      }
    />
  );
}

/**
 * The workspace's environment under its name: the usual ones in the member's language
 * ("Produktion"), anything else as the administrator wrote it.
 */
export function environmentLabel(environment: string): string {
  const key = `shell.env.${environment.trim().toLowerCase()}`;
  return hasMessage(key) ? t(key as `shell.env.${string}`) : environment;
}

// -- Pinned ---------------------------------------------------------------------------------------------

/**
 * The "+" of Pinned: pin or unpin the open record, page or dashboard; on a list, pin or unpin each of
 * its saved views; elsewhere, say what can be pinned.
 */
function usePinAddItems(): MenuItem[] {
  const { pinned, pin, unpin } = usePins();
  const current = useCurrentPin();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = useNav().data;
  const objectKey = /^\/o\/([^/]+)\/?$/.exec(pathname)?.[1];
  const listKey = objectKey ? decodeURIComponent(objectKey) : null;
  const views = useQuery({
    queryKey: ['views', listKey, 'rail'],
    queryFn: () => viewsClient.list(listKey!),
    enabled: Boolean(listKey),
    staleTime: 30_000,
  });
  const items: MenuItem[] = [];
  if (current.ref && current.ref.kind !== 'view')
    items.push({
      label: current.pinned ? t('shell.cmd.unpin') : t('shell.cmd.pin'),
      icon: 'star',
      onSelect: current.toggle,
    });
  if (listKey) {
    const object = nav?.apps.flatMap((a) => a.objects).find((o) => o.key === listKey);
    items.push({ type: 'label', label: t('shell.pin.viewsOf', { object: object?.label ?? listKey }) });
    for (const v of views.data ?? []) {
      const ref = { kind: 'view' as const, id: v.id, objectKey: listKey };
      const item = pinned.find((p) => pinMatches(p, ref) || pinMatches(p, { ...ref, id: v.key }));
      items.push({
        label: v.name,
        icon: 'view',
        checked: Boolean(item),
        onSelect: () => void (item ? unpin(item) : pin(ref)),
      });
    }
  }
  if (items.length === 0) items.push({ type: 'label', label: t('shell.pin.hint') });
  return items;
}

function PinnedSection() {
  const { pinned, unpin, reorder } = usePins();
  const addItems = usePinAddItems();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const href = useRouterState({ select: (s) => s.location.href });
  const list = useSortableList({
    id: 'rail-pinned',
    items: pinned,
    getKey: (p) => p.key,
    getLabel: (p) => p.label,
    onReorder: (from, to) => {
      const next = [...pinned];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      void reorder(next);
    },
  });
  return (
    <div className="rail-section" role="group" aria-labelledby="rail-pinned-title" data-testid="nav-pinned">
      <div className="rail-section-head">
        <h2 className="rail-section-title" id="rail-pinned-title">
          {t('shell.nav.pinned')}
        </h2>
        <Menu
          label={t('shell.pin.add')}
          width={240}
          placement="right-start"
          items={addItems}
          trigger={
            <button
              type="button"
              className="rail-group-add"
              aria-label={t('shell.pin.add')}
              data-testid="nav-pin-add"
            >
              <Icon name="plus" size={13} />
            </button>
          }
        />
      </div>
      {pinned.length === 0 ? <span className="rail-empty">{t('shell.nav.noPinned')}</span> : null}
      {list.description}
      <ul className="rail-list" aria-labelledby="rail-pinned-title">
        {list.ordered.map(({ item, key }) => {
          const edge = list.indicator(key);
          const isActive = item.href === href || item.href === pathname;
          return (
            <li key={key} className="rail-li" {...list.itemProps(key)}>
              <ContextMenu
                label={item.label}
                items={[
                  { label: t('shell.pin.unpin'), icon: 'x', onSelect: () => void unpin(item) },
                  { type: 'separator' },
                  ...list.moveItems(key),
                ]}
              >
                <ShortcutLink
                  href={item.href}
                  current={isActive}
                  data-testid={`nav-pinned-${item.key}`}
                  onKeyDown={(e) => {
                    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                      e.preventDefault();
                      list.moveBy(key, e.key === 'ArrowUp' ? -1 : 1);
                    }
                  }}
                >
                  <ItemBody icon={iconOf(item.icon, 'view')} tint={railTint(item.color)} label={item.label} />
                </ShortcutLink>
              </ContextMenu>
              <span className="rail-grip" {...list.handleProps(key)}>
                <Icon name="grip" size={12} />
              </span>
              {edge ? <DropIndicator edge={edge} /> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A rail shortcut (Pinned, Recent): a router link (no document load, preloading, ⌘-click for a new
 * tab) whose current state reads `aria-current="true"`. The rail's one `page` is its structural item
 * (the object, Home …); TanStack's `<Link>` would mark every active link `page`.
 */
function ShortcutLink({
  href,
  current,
  ref,
  ...rest
}: Omit<ComponentProps<'a'>, 'href'> & { href: string; current: boolean; ref?: Ref<HTMLAnchorElement> }) {
  const link = useLinkProps({ to: href }, ref as Ref<Element>) as Record<string, unknown>;
  const merged: Record<string, unknown> = { ...link, ...rest };
  // Both sides' handlers run: the router's (navigate, preload) and the caller's (context menu, keys).
  for (const [key, own] of Object.entries(rest))
    if (/^on[A-Z]/.test(key) && typeof own === 'function' && typeof link[key] === 'function') {
      const router = link[key] as (e: unknown) => void;
      merged[key] = (e: unknown) => {
        (own as (e: unknown) => void)(e);
        router(e);
      };
    }
  return (
    <a
      {...merged}
      className={`rail-item${current ? ' active' : ''}`}
      aria-current={current ? 'true' : undefined}
      data-status={undefined}
    />
  );
}

// -- Recent ---------------------------------------------------------------------------------------------

function RecentSection() {
  const recent = useRecent(8);
  const href = useRouterState({ select: (s) => s.location.href });
  const items: NavItem[] = (recent.data?.items ?? []).slice(0, 5);
  // Nothing opened yet (or still loading, or unreadable): no heading over an empty body and no hint at the
  // fold of a short window (the ⌘K bar and Home explain Recent); the section appears with the first item.
  if (items.length === 0) return null;
  return (
    <div className="rail-section" role="group" aria-labelledby="rail-recent-title" data-testid="nav-recent">
      <h2 className="rail-section-title" id="rail-recent-title">
        {t('shell.nav.recent')}
      </h2>
      {items.map((item) => (
        <ShortcutLink
          key={item.key}
          href={item.href}
          current={item.href === href}
          data-testid={`nav-recent-${item.key}`}
        >
          <ItemBody icon={iconOf(item.icon)} tint={railTint(item.color)} label={item.label} />
        </ShortcutLink>
      ))}
    </div>
  );
}

// -- Account menu ---------------------------------------------------------------------------------------

/** The rail's user card opens account-settings' account menu (the Account pages and Sign out). */
function RailAccount({ collapsed }: { collapsed: boolean }) {
  const me = useMe();
  return (
    <AccountMenu
      trigger={
        <button
          type="button"
          className="rail-user"
          aria-label={t('shell.account.menuOf', { name: me.member.name })}
          data-testid="nav-user"
        >
          <Avatar
            initials={me.member.initials}
            color={me.member.avatarColor}
            size={28}
            name={me.member.name}
          />
          {collapsed ? null : (
            <span className="rail-user-text">
              <span className="rail-user-name">{me.member.name}</span>
              <span className="rail-sub">{me.member.roleLabel}</span>
            </span>
          )}
          {collapsed ? null : <Icon name="chevron-updown" size={14} className="rail-ws-chevron" />}
        </button>
      }
    />
  );
}
